// src/components/artwork/tabs/ArtworkNFTTab.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BlockchainShowcase from "../../BlockchainShowcase";
import { useTranslation } from "react-i18next";
import { Palette, FileCheck2, Layers } from "lucide-react";

// Partenaire de certification/mint blockchain (SmartStamp) désactivé pour le
// moment — code conservé pour un futur partenaire. Passer à true pour réactiver.
const SHOW_SMARTSTAMP = false;

const ArtworkNFTTab = ({ formData, setFormData, readOnly = false, canEdit, artwork }) => {
  const { t } = useTranslation();
  const isReadOnly = canEdit !== undefined ? !canEdit : readOnly;
  // On part toujours d'un objet sûr (formData → artwork → {})
  const base = formData || artwork || {};
  const metadata = base.blockchainMetadata || {};

  const [, setActiveType] = useState(metadata.type || "");
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    if (isReadOnly || !setFormData) return;

    const prev = formData || {};
    const prevMeta = prev.blockchainMetadata || {};

    const updatedMeta = {
      ...prevMeta,
      [field]: value,
    };

    setFormData({
      ...prev,
      blockchainMetadata: updatedMeta,
    });
  };

  const blockchainTypes = [
    { value: "NFT", label: t("artworkNft.types.digital.label"), Icon: Palette, description: t("artworkNft.types.digital.description") },
    { value: "Certificat numérique", label: t("artworkNft.types.certificate.label"), Icon: FileCheck2, description: t("artworkNft.types.certificate.description") },
    { value: "Autre", label: t("artworkNft.types.hybrid.label"), Icon: Layers, description: t("artworkNft.types.hybrid.description") },
  ];

  const blockchains = [
    { value: "Ethereum", label: "Ethereum", color: "#627EEA" },
    { value: "Polygon", label: "Polygon", color: "#8247E5" },
    { value: "Tezos", label: "Tezos", color: "#2C7DF7" },
    { value: "Solana", label: "Solana", color: "#9945FF" },
    { value: "Flow", label: "Flow", color: "#00EF8B" },
    { value: "Autre", label: t("artworkNft.blockchainOther"), color: "#6B7280" },
  ];

  const certificateProviders = [
    { value: "Verisart", label: "Verisart" },
    { value: "Mintbase", label: "Mintbase" },
    { value: "SuperRare", label: "SuperRare" },
    { value: "Foundation", label: "Foundation" },
    { value: "OpenSea", label: "OpenSea" },
    { value: "Async Art", label: "Async Art" },
    { value: "KnownOrigin", label: "KnownOrigin" },
    { value: "Autre", label: t("artworkNft.providerOther") },
  ];

  const hasBlockchainMetadata =
    metadata && Object.keys(metadata).length > 0;

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center border-b border-gray-100 pb-6">
        <h2 className="title-artsy text-2xl text-gray-800 mb-2">
          {t("artworkNft.title")}
        </h2>
        <p className="text-gray-600 font-serif italic">
          {t("artworkNft.subtitle")}
        </p>
      </div>

      {/* Ce que fait réellement ARTKIV côté blockchain (pas d'overpromise) */}
      <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">
          {t("artworkNft.info.title")}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed">
          {t("artworkNft.info.body")}
        </p>
        <button
          type="button"
          onClick={() => navigate("/blockchain-demo")}
          className="mt-3 text-sm font-medium text-gray-900 underline underline-offset-2 hover:text-black"
        >
          {t("artworkNft.info.fingerprintCta")}
        </button>
      </div>

      {/* Partenaire de certification/mint blockchain — désactivé (SHOW_SMARTSTAMP) */}
      {SHOW_SMARTSTAMP && !hasBlockchainMetadata && (
        <div className="mt-8 p-6 border border-gray-200 rounded-xl bg-gray-50">
          <h3 className="text-lg font-semibold mb-2">
            {t("artworkNft.smartstamp.title")}
          </h3>
          <p className="text-gray-600 mb-4">
            {t("artworkNft.smartstamp.body")}
          </p>
          <button
            type="button"
            onClick={() => navigate("/blockchain-demo")}
            className="px-5 py-2.5 bg-black text-white rounded-xl hover:bg-gray-900 transition"
          >
            {t("artworkNft.smartstamp.cta")}
          </button>
        </div>
      )}

      {/* Select type */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t("artworkNft.certificationType")}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {blockchainTypes.map((type) => (
            <div
              key={type.value}
              onClick={() =>
                !isReadOnly &&
                (setActiveType(type.value), handleChange("type", type.value))
              }
              className={`relative rounded-lg border-2 p-6 transition-all duration-300 
                ${
                  metadata.type === type.value
                    ? "border-gray-800 bg-gray-50 shadow-lg"
                    : "border-gray-200 bg-white hover:border-gray-400 hover:shadow-md"
                }
                ${isReadOnly ? "cursor-default" : "cursor-pointer"}
              `}
            >
              <div className="text-center">
                <type.Icon className="w-7 h-7 mx-auto mb-3 text-gray-700" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="title-artsy text-lg text-gray-800 mb-2">
                  {type.label}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {type.description}
                </p>
              </div>

              {metadata.type === type.value && (
                <div className="absolute top-2 right-2">
                  <div className="w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Details Section */}
      {metadata.type && (
        <div className="space-y-6 bg-gray-50 rounded-lg p-6 border border-gray-200">
          {/* Blockchain selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Blockchain
            </label>
            <div className="flex flex-wrap gap-3">
              {blockchains.map((bc) => (
                <button
                  key={bc.value}
                  type="button"
                  disabled={isReadOnly}
                  onClick={() => handleChange("blockchain", bc.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border 
                    ${
                      metadata.blockchain === bc.value
                        ? "bg-gray-800 text-white border-gray-800 shadow-lg"
                        : "bg-white text-gray-700 hover:shadow-md border-gray-300 hover:border-gray-400"
                    }
                    ${isReadOnly ? "cursor-default" : "cursor-pointer"}
                  `}
                >
                  {bc.label}
                </button>
              ))}
            </div>
          </div>

          {/* Form fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contract */}
            <div className="space-y-2">
              <label className="label-artsy">{t("artworkNft.contractAddress")}</label>
              <input
                type="text"
                disabled={isReadOnly}
                value={metadata.contractAddress || ""}
                onChange={(e) =>
                  handleChange("contractAddress", e.target.value)
                }
                className="form-control-artsy w-full font-mono text-sm"
                placeholder="0x..."
              />
            </div>

            {/* Token ID */}
            <div className="space-y-2">
              <label className="label-artsy">{t("artworkNft.tokenId")}</label>
              <input
                type="text"
                disabled={isReadOnly}
                value={metadata.tokenId || ""}
                onChange={(e) => handleChange("tokenId", e.target.value)}
                className="form-control-artsy w-full"
                placeholder={t("artworkNft.tokenPlaceholder")}
              />
            </div>

            {/* Provider */}
            <div className="space-y-2">
              <label className="label-artsy">{t("artworkNft.provider")}</label>
              <select
                disabled={isReadOnly}
                value={metadata.certificateProvider || ""}
                onChange={(e) =>
                  handleChange("certificateProvider", e.target.value)
                }
                className="form-control-artsy w-full"
              >
                <option value="">{t("artworkNft.providerSelect")}</option>
                {certificateProviders.map((provider) => (
                  <option key={provider.value} value={provider.value}>
                    {provider.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Mint date */}
            <div className="space-y-2">
              <label className="label-artsy">{t("artworkNft.mintDate")}</label>
              <input
                type="date"
                disabled={isReadOnly}
                value={metadata.mintDate || ""}
                onChange={(e) => handleChange("mintDate", e.target.value)}
                className="form-control-artsy w-full"
              />
            </div>
          </div>

          {/* Explorer URL */}
          <div className="space-y-2">
            <label className="label-artsy">{t("artworkNft.explorerLink")}</label>
            <input
              type="url"
              disabled={isReadOnly}
              value={metadata.explorerUrl || ""}
              onChange={(e) => handleChange("explorerUrl", e.target.value)}
              className="form-control-artsy w-full"
              placeholder="https://etherscan.io/token/..."
            />
          </div>

          {/* Link to physical */}
          <div className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                {t("artworkNft.linkedPhysicalTitle")}
              </h4>
              <p className="text-sm text-gray-500">
                {t("artworkNft.linkedPhysicalDescription")}
              </p>
            </div>
            <button
              type="button"
              disabled={isReadOnly}
              onClick={() =>
                handleChange(
                  "linkedToPhysicalWork",
                  !metadata.linkedToPhysicalWork
                )
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full 
                ${
                  metadata.linkedToPhysicalWork ? "bg-gray-800" : "bg-gray-300"
                }
              `}
            >
              <span
                className={`inline-block h-4 w-4 bg-white rounded-full transform 
                  ${
                    metadata.linkedToPhysicalWork
                      ? "translate-x-6"
                      : "translate-x-1"
                  }
                `}
              />
            </button>
          </div>
        </div>
      )}

      {/* READ ONLY DISPLAY */}
      {isReadOnly && hasBlockchainMetadata && (
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">
            {t("artworkNft.certificateTitle")}
          </h3>
          <BlockchainShowcase metadata={metadata} />
        </div>
      )}
    </div>
  );
};

export default ArtworkNFTTab;
